import { ApexOptions } from 'apexcharts';
import { neutral } from '../../theme/colors';
import { activeCurrency, formatMoney, formatMoneyCompact } from '../../utils/money';

/**
 * Chart palette.
 *
 * `categorical` slots are assigned in fixed order and never cycled — colour
 * follows the entity, not its rank, so filtering a series never repaints the
 * survivors. The four slots are validated against the light chart surface for
 * colour-vision deficiency (worst adjacent pair ΔE 10.3 deutan, normal-vision
 * ΔE 17.0, all four ≥ 3:1 contrast).
 *
 * `status` is reserved: it is only ever used where the colour *means* a state
 * (inventory stock levels, period deltas), never as a series colour.
 */
export const palette = {
	categorical: ['#6366F1', '#0891B2', '#DB2777', '#7C3AED'],
	/** Single hue for one-series magnitude charts — every bar the same colour. */
	single: '#6366F1',
	/** Context/comparison marks that must recede behind the primary series. */
	muted: neutral[300],
	status: {
		positive: '#10B981',
		warning: '#F79009',
		critical: '#F04438',
		info: '#06AED4',
		neutral: neutral[400],
	},
	grid: '#EEF0F4',
	axis: neutral[400],
	label: neutral[500],
};

/**
 * Whole amounts in the active location's currency: ₹1,23,456 (Indian
 * grouping) for INR, AED 123,456 elsewhere. The names predate currency being
 * a setting and are kept so every chart and tile keeps working unchanged.
 */
export const inr = (value?: number | null): string => formatMoney(Math.round(value ?? 0), activeCurrency(), 0);

/** Axis-scale currency: ₹950 · ₹12.4K · ₹3.5L · ₹1.2Cr for INR; K/M/B elsewhere. */
export const inrCompact = (value?: number | null): string => formatMoneyCompact(value ?? 0, activeCurrency());

export const num = (value?: number | null): string =>
	Math.round(value ?? 0).toLocaleString('en-IN');

export const pct = (value?: number | null, digits = 1): string =>
	`${(value ?? 0).toFixed(digits)}%`;

/**
 * Shared Apex baseline: recessive hairline grid, no chart toolbar (export lives
 * in the page header, and zoom on a handful of points is noise), tooltips on by
 * default, data labels off — they are re-enabled selectively per chart.
 */
export const baseChartOptions = (): ApexOptions => ({
	chart: {
		fontFamily: 'inherit',
		background: 'transparent',
		toolbar: { show: false },
		zoom: { enabled: false },
		animations: { enabled: true, speed: 320 },
		parentHeightOffset: 0,
	},
	dataLabels: { enabled: false },
	grid: {
		borderColor: palette.grid,
		strokeDashArray: 0,
		xaxis: { lines: { show: false } },
		yaxis: { lines: { show: true } },
		padding: { top: 0, right: 8, bottom: 0, left: 8 },
	},
	legend: {
		show: false,
	},
	states: {
		hover: { filter: { type: 'lighten', value: 0.06 } },
		active: { filter: { type: 'none' } },
	},
	tooltip: {
		theme: 'light',
		style: { fontSize: '12px', fontFamily: 'inherit' },
	},
	xaxis: {
		axisBorder: { show: false },
		axisTicks: { show: false },
		labels: {
			style: { colors: palette.label, fontSize: '12px', fontFamily: 'inherit' },
		},
		tooltip: { enabled: false },
	},
	yaxis: {
		labels: {
			style: { colors: palette.label, fontSize: '12px', fontFamily: 'inherit' },
		},
	},
});
